'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { GuestReview, CustomerReview, PartnerReview } from '@/content/reviews';
import styles from './EnhancedTestimonials.module.css';

type ReviewType = 'all' | 'guest' | 'customer' | 'partner';

interface EnhancedTestimonialsProps {
  guestReviews: GuestReview[];
  customerReviews: CustomerReview[];
  partnerReviews: PartnerReview[];
  currentLocale: 'en' | 'nl' | 'de';
  translations: {
    sectionTitle: string;
    allTitle: string;
    guestsTitle: string;
    customersTitle: string;
    partnersTitle: string;
    viewAllReviews: string;
  };
}

export default function EnhancedTestimonials({
  guestReviews,
  customerReviews,
  partnerReviews,
  currentLocale,
  translations,
}: EnhancedTestimonialsProps) {
  const [activeTab, setActiveTab] = useState<ReviewType>('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Select reviews based on active tab
  const getCurrentReviews = () => {
    switch (activeTab) {
      case 'all':
        // Interleave reviews: customer → guest → partner → customer → guest → partner...
        const allReviews: Array<{ type: 'customer' | 'guest' | 'partner'; data: any }> = [];
        const maxLength = Math.max(
          customerReviews.length,
          guestReviews.length,
          partnerReviews.length
        );
        
        for (let i = 0; i < maxLength; i++) {
          if (i < customerReviews.length) {
            allReviews.push({ type: 'customer' as const, data: customerReviews[i] });
          }
          if (i < guestReviews.length) {
            allReviews.push({ type: 'guest' as const, data: guestReviews[i] });
          }
          if (i < partnerReviews.length) {
            allReviews.push({ type: 'partner' as const, data: partnerReviews[i] });
          }
        }
        
        return allReviews.slice(0, 12); // Limit to 12 reviews in ALL tab
      case 'guest':
        return guestReviews.slice(0, 6).map(r => ({ type: 'guest' as const, data: r }));
      case 'customer':
        return customerReviews.slice(0, 4).map(r => ({ type: 'customer' as const, data: r }));
      case 'partner':
        return partnerReviews.slice(0, 4).map(r => ({ type: 'partner' as const, data: r }));
      default:
        return [];
    }
  };

  const reviews = getCurrentReviews();

  // Reset index when changing tabs
  useEffect(() => {
    setCurrentIndex(0);
  }, [activeTab]);

  const handleNext = useCallback(() => {
    if (isTransitioning || reviews.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, reviews.length]);

  const handlePrev = () => {
    if (isTransitioning || reviews.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  // Auto-rotate
  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 7000);
    return () => clearInterval(interval);
  }, [handleNext]);

  const currentReview = reviews[currentIndex];

  // Render review content based on type
  const renderReview = () => {
    if (!currentReview) return null;

    const reviewType = currentReview.type;
    const reviewData = currentReview.data;

    if (reviewType === 'guest') {
      const review = reviewData as GuestReview;
      const reviewText = review[`review_${currentLocale}` as keyof GuestReview] as string;
      
      // Check if translation is needed
      const originalLang = review.originalLanguage;
      const isTranslated = originalLang && originalLang !== currentLocale;
      
      return (
        <div className={styles.reviewCard}>
          <div className={styles.guestHeader}>
            <div 
              className={styles.profileCircle}
              style={{ backgroundColor: `hsl(${review.profileLetter.charCodeAt(0) * 15}, 70%, 60%)` }}
            >
              {review.profileLetter}
            </div>
            <div className={styles.reviewerInfo}>
              <div className={styles.reviewerName}>{review.name}</div>
              <div className={styles.stars}>
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={styles.starFilled}>
                    ★
                  </span>
                ))}
              </div>
            </div>
          </div>
          <blockquote className={styles.quote}>{reviewText}</blockquote>
          
          {isTranslated && (
            <div className={styles.translationBadge}>
              <span className={styles.flagWrapper}>
                <Image
                  src={originalLang === 'de' ? '/images/flags/de.svg' : originalLang === 'en' ? '/images/flags/gb.svg' : '/images/flags/nl.svg'}
                  alt={originalLang}
                  width={16}
                  height={12}
                  className={styles.flag}
                />
              </span>
              <span>
                {currentLocale === 'nl' ? 'Vertaald uit ' : currentLocale === 'de' ? 'Übersetzt aus ' : 'Translated from '}
                {originalLang === 'de' ? (currentLocale === 'nl' ? 'Duits' : currentLocale === 'de' ? 'Deutsch' : 'German') : 
                 originalLang === 'en' ? (currentLocale === 'nl' ? 'Engels' : currentLocale === 'de' ? 'Englisch' : 'English') : 
                 (currentLocale === 'nl' ? 'Nederlands' : currentLocale === 'de' ? 'Niederländisch' : 'Dutch')}
              </span>
            </div>
          )}
          
          <div className={styles.sourceWithLogo}>
            <svg className={styles.googleIcon} viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Google Review</span>
          </div>
        </div>
      );
    }

    if (reviewType === 'customer') {
      const review = reviewData as CustomerReview;
      const reviewText = review[`review_${currentLocale}` as keyof CustomerReview] as string;
      
      // Check if translation is needed
      const originalLang = review.originalLanguage;
      const isTranslated = originalLang && originalLang !== currentLocale;
      
      return (
        <div className={styles.reviewCard}>
          <div className={styles.customerHeader}>
            <div 
              className={styles.profileCircle}
              style={{ backgroundColor: `hsl(${review.profileLetter.charCodeAt(0) * 15}, 70%, 60%)` }}
            >
              {review.profileLetter}
            </div>
            <div className={styles.reviewerInfo}>
              <div className={styles.reviewerName}>{review.name}</div>
              <div className={styles.rating}>{review.rating}</div>
            </div>
          </div>
          <blockquote className={styles.quote}>{reviewText}</blockquote>
          
          {isTranslated && (
            <div className={styles.translationBadge}>
              <span className={styles.flagWrapper}>
                <Image
                  src={originalLang === 'de' ? '/images/flags/de.svg' : originalLang === 'en' ? '/images/flags/gb.svg' : '/images/flags/nl.svg'}
                  alt={originalLang}
                  width={16}
                  height={12}
                  className={styles.flag}
                />
              </span>
              <span>
                {currentLocale === 'nl' ? 'Vertaald uit ' : currentLocale === 'de' ? 'Übersetzt aus ' : 'Translated from '}
                {originalLang === 'de' ? (currentLocale === 'nl' ? 'Duits' : currentLocale === 'de' ? 'Deutsch' : 'German') : 
                 originalLang === 'en' ? (currentLocale === 'nl' ? 'Engels' : currentLocale === 'de' ? 'Englisch' : 'English') : 
                 (currentLocale === 'nl' ? 'Nederlands' : currentLocale === 'de' ? 'Niederländisch' : 'Dutch')}
              </span>
            </div>
          )}
          
          <div className={styles.source}>Jamezz Support</div>
        </div>
      );
    }

    if (reviewType === 'partner') {
      const review = reviewData as PartnerReview;
      // Use short summary if available, otherwise use full text
      const shortSummaryKey = `shortSummary_${currentLocale}` as keyof PartnerReview;
      const reviewKey = `review_${currentLocale}` as keyof PartnerReview;
      
      const shortSummary = (review[shortSummaryKey] as string) || '';
      const fullReview = (review[reviewKey] as string) || review.review_nl;
      
      // Use short summary for homepage, full text if not available
      const displayText = shortSummary || fullReview;
      
      // Check if translation is needed
      const originalLang = review.originalLanguage;
      const isTranslated = originalLang && originalLang !== currentLocale;
      
      return (
        <div className={styles.reviewCard}>
          <div className={styles.partnerHeader}>
            {review.logo ? (
              <div className={styles.partnerLogo}>
                <Image
                  src={review.logo}
                  alt={`${review.company} logo`}
                  width={80}
                  height={80}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            ) : (
              <div className={styles.partnerLogoPlaceholder}>
                {review.company.charAt(0)}
              </div>
            )}
            <div className={styles.partnerInfo}>
              <div className={styles.companyName}>{review.company}</div>
              {review.person && (
                <div className={styles.personInfo}>
                  {review.person} {review.role && `— ${review.role}`}
                </div>
              )}
            </div>
          </div>
          <blockquote className={styles.quote}>{displayText}</blockquote>
          
          {isTranslated && (
            <div className={styles.translationBadge}>
              <span className={styles.flagWrapper}>
                <Image
                  src={originalLang === 'de' ? '/images/flags/de.svg' : originalLang === 'en' ? '/images/flags/gb.svg' : '/images/flags/nl.svg'}
                  alt={originalLang}
                  width={16}
                  height={12}
                  className={styles.flag}
                />
              </span>
              <span>
                {currentLocale === 'nl' ? 'Vertaald uit ' : currentLocale === 'de' ? 'Übersetzt aus ' : 'Translated from '}
                {originalLang === 'de' ? (currentLocale === 'nl' ? 'Duits' : currentLocale === 'de' ? 'Deutsch' : 'German') : 
                 originalLang === 'en' ? (currentLocale === 'nl' ? 'Engels' : currentLocale === 'de' ? 'Englisch' : 'English') : 
                 (currentLocale === 'nl' ? 'Nederlands' : currentLocale === 'de' ? 'Niederländisch' : 'Dutch')}
              </span>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>{translations.sectionTitle}</h2>

        {/* Tab Navigation */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'all' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('all')}
          >
            {translations.allTitle}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'customer' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('customer')}
          >
            {translations.customersTitle}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'guest' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('guest')}
          >
            {translations.guestsTitle}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'partner' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('partner')}
          >
            {translations.partnersTitle}
          </button>
        </div>

        {/* Carousel */}
        <div className={styles.carousel}>
          <button
            className={`${styles.navButton} ${styles.navButtonPrev}`}
            onClick={handlePrev}
            aria-label="Previous review"
            disabled={reviews.length === 0}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className={styles.reviewContent}>
            {renderReview()}
          </div>

          <button
            className={`${styles.navButton} ${styles.navButtonNext}`}
            onClick={handleNext}
            aria-label="Next review"
            disabled={reviews.length === 0}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {/* Indicators */}
        <div className={styles.indicators}>
          {reviews.map((_, index) => (
            <button
              key={index}
              className={`${styles.indicator} ${index === currentIndex ? styles.activeIndicator : ''}`}
              onClick={() => {
                if (!isTransitioning) {
                  setIsTransitioning(true);
                  setCurrentIndex(index);
                  setTimeout(() => setIsTransitioning(false), 500);
                }
              }}
              aria-label={`Go to review ${index + 1}`}
            />
          ))}
        </div>

        {/* View All Link */}
        <div className={styles.viewAll}>
          <Link href={currentLocale === 'nl' ? '/nl/beoordelingen' : currentLocale === 'de' ? '/de/bewertungen' : '/reviews'} className={styles.viewAllLink}>
            {translations.viewAllReviews}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}

